import { useEffect } from 'react';

import { INPUT_METHOD } from '@atlaskit/editor-common/analytics/types/enums';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AIPlugin } from '../../../aiPluginType';
import { beforeDispatchAIRovoAgentTransaction } from '../../prebuilt/config-items/utils/action-utils';

let lastActiveEditorView: EditorView | null = null;

// Tracks whether the user has explicitly clicked into each editor instance at
// least once. Used to determine if insertable content should be appended at page end
const focusedEditorViews = new WeakMap<EditorView, boolean>();

export const hasEditorBeenFocused = (editorView: EditorView): boolean =>
	!!focusedEditorViews.get(editorView);

export const updateLastActiveEditorView = (editorView: EditorView): void => {
	lastActiveEditorView = editorView;
};

export const isLastActiveEditorView = (editorView: EditorView): boolean =>
	editorView === lastActiveEditorView;

export const createDispatchTransaction = (
	editorView: EditorView,
	editorApi: ExtractInjectionAPI<AIPlugin> | undefined,
	scrollToEnd: boolean = false,
) => {
	return (tr: Transaction): void => {
		const { state } = editorView;
		let finalTr = tr;

		if (editorApi?.card?.actions.queueCardsFromChangedTr) {
			finalTr = editorApi.card.actions.queueCardsFromChangedTr(state, finalTr, INPUT_METHOD.MANUAL);
		}
		beforeDispatchAIRovoAgentTransaction(finalTr);
		// When content is appended to the end of the document (user hasn't clicked
		// into the editor), move the selection to the end so scrollIntoView() brings
		// the new content into view rather than staying at the default cursor (pos 1).
		if (scrollToEnd) {
			try {
				const endPos = finalTr.doc.content.size;
				finalTr.setSelection(TextSelection.near(finalTr.doc.resolve(endPos), -1));
			} catch (e) {
				if (!(e instanceof RangeError)) {
					throw e;
				}
				// Use scroll into view as fallback
				finalTr.scrollIntoView();
			}
		} else {
			finalTr.scrollIntoView();
		}
		editorView.dispatch(finalTr);
		editorView.focus();

		// Instrumentation to scroll to end if content is appended to page
		if (scrollToEnd) {
			requestAnimationFrame(() => {
				try {
					const { selection } = editorView.state;
					const domAtEnd = editorView.domAtPos(selection.from);
					const node =
						domAtEnd.node instanceof Element ? domAtEnd.node : domAtEnd.node.parentElement;
					if (node) {
						node.scrollIntoView({ behavior: 'smooth', block: 'end' });
					}
				} catch {
					// domAtPos can throw if the position is out of range; ignore silently.
				}
			});
		}
	};
};

export const useTrackLastActiveEditorView = (editorView: EditorView): void => {
	useEffect(() => {
		if (lastActiveEditorView === null) {
			updateLastActiveEditorView(editorView);
		}

		let editorIsFocused = false;

		const handleFocus = (): void => {
			editorIsFocused = true;
			updateLastActiveEditorView(editorView);
		};
		const handleBlur = (): void => {
			editorIsFocused = false;
		};

		// Set focusedEditorViews when the user explicitly interacts with the editor.
		const handleClick = (): void => {
			focusedEditorViews.set(editorView, true);
		};
		const handleKeydown = (): void => {
			if (editorIsFocused) {
				focusedEditorViews.set(editorView, true);
			}
		};

		const editorDom = editorView.dom;
		// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
		editorDom.addEventListener('focus', handleFocus);
		// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
		editorDom.addEventListener('blur', handleBlur);
		// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
		editorDom.addEventListener('click', handleClick);
		// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
		editorDom.addEventListener('keydown', handleKeydown);
		return () => {
			// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
			editorDom.removeEventListener('focus', handleFocus);
			// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
			editorDom.removeEventListener('blur', handleBlur);
			// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
			editorDom.removeEventListener('click', handleClick);
			// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
			editorDom.removeEventListener('keydown', handleKeydown);

			// Clear the last active editor view on unmount so stale references
			// don't persist after the editor is destroyed.
			if (lastActiveEditorView === editorView) {
				lastActiveEditorView = null;
			}
			focusedEditorViews.delete(editorView);
		};
	}, [editorView]);
};
